import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/DropdownMenu/DropdownMenu';
import type { SortDirection, SortState } from '@/hooks/useSort';
import { cn } from '@/util/utils';

/** Radix will not take an empty value, so the list's own order travels under this one. */
const OWN_ORDER = '-';

type TableSortControlProps<K extends string> = {
    /** The sortable columns, in the table's order, labelled as the table labels them. */
    options: { key: K; label: string }[];
    sort: SortState<K> | null;
    onChange: (sort: SortState<K> | null) => void;
    /** The button's name while nothing sorts, and the menu's heading. */
    label?: string;
    ownOrderLabel?: string;
    className?: string;
};

/**
 * Sorting for a stacked table, whose header (and with it the sortable labels) is gone below
 * 768. One button on the search's row: an arrow pair while the list is in its own order, the
 * sorted column's name and direction once it is not, so the state reads without opening the
 * menu. The menu is the column list and the direction, as radios.
 */
function TableSortControl<K extends string>({
    options,
    sort,
    onChange,
    label = 'Sort',
    ownOrderLabel = 'Default order',
    className,
}: TableSortControlProps<K>) {
    const active = sort ? options.find((option) => option.key === sort.key) : undefined;
    const Arrow = sort?.direction === 'desc' ? ArrowDown : ArrowUp;
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                {active ? (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={cn('shrink-0', className)}
                        aria-label={`${label}: ${active.label}, ${sort?.direction === 'desc' ? 'descending' : 'ascending'}`}
                    >
                        <Arrow />
                        {active.label}
                    </Button>
                ) : (
                    <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        className={cn('shrink-0', className)}
                        aria-label={label}
                    >
                        <ArrowUpDown />
                    </Button>
                )}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuLabel>{label}</DropdownMenuLabel>
                <DropdownMenuRadioGroup
                    value={sort?.key ?? OWN_ORDER}
                    onValueChange={(value) =>
                        onChange(
                            value === OWN_ORDER
                                ? null
                                : { key: value as K, direction: sort?.direction ?? 'asc' },
                        )
                    }
                >
                    <DropdownMenuRadioItem value={OWN_ORDER}>{ownOrderLabel}</DropdownMenuRadioItem>
                    {options.map((option) => (
                        <DropdownMenuRadioItem key={option.key} value={option.key}>
                            {option.label}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup
                    value={sort?.direction ?? 'asc'}
                    onValueChange={(value) =>
                        sort && onChange({ ...sort, direction: value as SortDirection })
                    }
                >
                    <DropdownMenuRadioItem value="asc" disabled={!sort}>
                        Ascending
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="desc" disabled={!sort}>
                        Descending
                    </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export { TableSortControl, type TableSortControlProps };
